const withPinDefaults = (pin) => ({
  pin_number: pin.pin_number,
  pin_name: pin.pin_name || `PIN${pin.pin_number}`,
  pin_role: pin.pin_role || '',
  functions: pin.functions || '',
  alternate_pin_names: pin.alternate_pin_names || '',
  alternate_functions: pin.alternate_functions || '',
  description: pin.description || '',
  current_limit_ma: pin.current_limit_ma || '',
  voltage_min_v: pin.voltage_min_v || '',
  voltage_max_v: pin.voltage_max_v || '',
  is_5v_tolerant: Boolean(pin.is_5v_tolerant),
  capability_tags: Array.isArray(pin.capability_tags) ? pin.capability_tags : []
});

export const IC_PIN_PRESETS = {
  '74hc595-dip-16': {
    label: '74HC595 - DIP-16',
    name: '74HC595',
    slug: '74hc595',
    package_type: 'DIP',
    pin_count: 16,
    marker_style: 'notch-dot',
    label_mode: 'name',
    pins: [
      { pin_number: 1, pin_name: 'QB', pin_role: 'output', functions: 'Parallel output B', capability_tags: ['digital-output'] },
      { pin_number: 2, pin_name: 'QC', pin_role: 'output', functions: 'Parallel output C', capability_tags: ['digital-output'] },
      { pin_number: 3, pin_name: 'QD', pin_role: 'output', functions: 'Parallel output D', capability_tags: ['digital-output'] },
      { pin_number: 4, pin_name: 'QE', pin_role: 'output', functions: 'Parallel output E', capability_tags: ['digital-output'] },
      { pin_number: 5, pin_name: 'QF', pin_role: 'output', functions: 'Parallel output F', capability_tags: ['digital-output'] },
      { pin_number: 6, pin_name: 'QG', pin_role: 'output', functions: 'Parallel output G', capability_tags: ['digital-output'] },
      { pin_number: 7, pin_name: 'QH', pin_role: 'output', functions: 'Parallel output H', capability_tags: ['digital-output'] },
      { pin_number: 8, pin_name: 'GND', pin_role: 'ground', functions: 'Ground', capability_tags: ['ground'] },
      { pin_number: 9, pin_name: "QH'", pin_role: 'output', functions: 'Serial output for cascading another 74HC595', capability_tags: ['digital-output'] },
      { pin_number: 10, pin_name: 'MR', pin_role: 'control', functions: 'Master reset, active low', capability_tags: ['reset', 'active-low'] },
      { pin_number: 11, pin_name: 'SH_CP', pin_role: 'clock', functions: 'Shift register clock input', capability_tags: ['clock'] },
      { pin_number: 12, pin_name: 'ST_CP', pin_role: 'clock', functions: 'Storage register clock input', capability_tags: ['clock'] },
      { pin_number: 13, pin_name: 'OE', pin_role: 'control', functions: 'Output enable, active low', capability_tags: ['enable', 'active-low'] },
      { pin_number: 14, pin_name: 'DS', pin_role: 'input', functions: 'Serial data input', capability_tags: ['digital-input', 'spi-mosi'] },
      { pin_number: 15, pin_name: 'QA', pin_role: 'output', functions: 'Parallel output A', capability_tags: ['digital-output'] },
      { pin_number: 16, pin_name: 'VCC', pin_role: 'power', functions: 'Positive supply voltage', capability_tags: ['power'] }
    ].map(withPinDefaults)
  },

  'ne555-dip-8': {
    label: 'NE555 Timer - DIP-8',
    name: 'NE555',
    slug: 'ne555',
    package_type: 'DIP',
    pin_count: 8,
    marker_style: 'notch-dot',
    label_mode: 'name',
    pins: [
      { pin_number: 1, pin_name: 'GND', pin_role: 'ground', functions: 'Ground', capability_tags: ['ground'] },
      { pin_number: 2, pin_name: 'TRIG', pin_role: 'input', functions: 'Trigger input', capability_tags: ['digital-input'] },
      { pin_number: 3, pin_name: 'OUT', pin_role: 'output', functions: 'Timer output', capability_tags: ['digital-output'] },
      { pin_number: 4, pin_name: 'RESET', pin_role: 'reset', functions: 'Reset input, active low', capability_tags: ['reset', 'active-low'] },
      { pin_number: 5, pin_name: 'CTRL', pin_role: 'control', functions: 'Control voltage input', capability_tags: ['analog-input'] },
      { pin_number: 6, pin_name: 'THRES', pin_role: 'input', functions: 'Threshold input', capability_tags: ['analog-input'] },
      { pin_number: 7, pin_name: 'DISCH', pin_role: 'control', functions: 'Discharge transistor pin', capability_tags: ['open-drain'] },
      { pin_number: 8, pin_name: 'VCC', pin_role: 'power', functions: 'Positive supply voltage', capability_tags: ['power'] }
    ].map(withPinDefaults)
  },

  'lm358-dip-8': {
    label: 'LM358 Op-Amp - DIP-8',
    name: 'LM358',
    slug: 'lm358',
    package_type: 'DIP',
    pin_count: 8,
    marker_style: 'notch-dot',
    label_mode: 'name',
    pins: [
      { pin_number: 1, pin_name: 'OUT1', pin_role: 'output', functions: 'Output of op-amp 1', capability_tags: ['analog-output'] },
      { pin_number: 2, pin_name: 'IN1-', pin_role: 'input', functions: 'Inverting input of op-amp 1', capability_tags: ['analog-input'] },
      { pin_number: 3, pin_name: 'IN1+', pin_role: 'input', functions: 'Non-inverting input of op-amp 1', capability_tags: ['analog-input'] },
      { pin_number: 4, pin_name: 'V-', pin_role: 'ground', functions: 'Negative supply or ground', capability_tags: ['ground'] },
      { pin_number: 5, pin_name: 'IN2+', pin_role: 'input', functions: 'Non-inverting input of op-amp 2', capability_tags: ['analog-input'] },
      { pin_number: 6, pin_name: 'IN2-', pin_role: 'input', functions: 'Inverting input of op-amp 2', capability_tags: ['analog-input'] },
      { pin_number: 7, pin_name: 'OUT2', pin_role: 'output', functions: 'Output of op-amp 2', capability_tags: ['analog-output'] },
      { pin_number: 8, pin_name: 'V+', pin_role: 'power', functions: 'Positive supply voltage', capability_tags: ['power'] }
    ].map(withPinDefaults)
  }
};

export const getIcPinPresetOptions = () => (
  Object.entries(IC_PIN_PRESETS).map(([value, preset]) => ({
    value,
    label: preset.label
  }))
);